import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';
import { Task } from '@/lib/sdk/chatbot/types';
import { GridRow } from './useTaskGridReducer';
import { WorkflowTaskGrid, WorkflowTaskGridHandle } from './WorkflowTaskGrid';
import { TaskConfigPanel } from './TaskConfigPanel';

const VALIDATE_ID = 'subtask-editor-grid-validate';

interface SubtaskEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTasks: Task[];
  onSave: (tasks: Task[]) => void;
  parentTaskName?: string;
}

export function SubtaskEditorModal({ isOpen, onClose, initialTasks, onSave, parentTaskName }: SubtaskEditorModalProps) {
  const gridRef = useRef<WorkflowTaskGridHandle>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelRow, setPanelRow] = useState<GridRow | null>(null);
  const [panelRowIdx, setPanelRowIdx] = useState(0);
  const modalGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const handleValidRows = useCallback((tasks: Task[]) => {
    onSave(tasks.map((task) => ({ ...task, description: task.description ?? '' })));
  }, [onSave]);

  const handleOpenConfigPanel = useCallback((row: GridRow, idx: number) => {
    setPanelRow(row);
    setPanelRowIdx(idx);
    setPanelOpen(true);
  }, []);

  const handlePanelSave = useCallback((localId: string, config: Partial<GridRow>) => {
    gridRef.current?.dispatchAdvanced(localId, config);
  }, []);

  const panelExistingTasks = panelRow
    ? (gridRef.current?.getRows() ?? [])
        .slice(0, panelRowIdx)
        .filter((r) => r.name.trim())
        .map((r, i) => ({ id: r.id, name: r.name, index: i + 1, type: r.type }))
    : [];

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="full">
        <DrawerOverlay backdropFilter="blur(2px)" />
        <DrawerContent
          data-testid="wfv4-subtask-modal"
          bg="bg-surface"
          bgGradient={modalGradient}
          h="65vh"
          borderTopRadius="xl"
          borderTopWidth="1px"
          borderColor="border-default"
        >
          <DrawerHeader borderBottomWidth="1px" borderColor="border-default">
            Subtasks
            {parentTaskName && (
              <Text as="span" fontSize="sm" fontWeight="normal" color="text-secondary" ml={2}>
                for "{parentTaskName}"
              </Text>
            )}
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody py={4} overflowY="auto">
            <WorkflowTaskGrid
              ref={gridRef}
              template="form"
              chatbotLifecycle="daily"
              initialTasks={initialTasks}
              onValidRows={handleValidRows}
              onValidationError={() => {}}
              validateTriggerId={VALIDATE_ID}
              onOpenConfigPanel={handleOpenConfigPanel}
            />
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" borderColor="border-default" gap={3}>
            <Button variant="ghost" onClick={onClose} data-testid="wfv4-subtask-modal-cancel-btn">Cancel</Button>
            <Button
              colorScheme="brand"
              data-testid="wfv4-subtask-modal-save-btn"
              onClick={() => document.getElementById(VALIDATE_ID)?.click()}
            >
              Save Subtasks
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <TaskConfigPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        row={panelRow}
        rowIdx={panelRowIdx}
        onSave={handlePanelSave}
        existingTasks={panelExistingTasks}
        chatbotLifecycle="daily"
      />
    </>
  );
}
